import React, { forwardRef, useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Native <select> styled to match Input geometry, with a custom chevron.
 * `options`: [{ value, label, disabled? }]
 * Or provide children (native <option> elements).
 */
export const Select = forwardRef(function Select(
  {
    label,
    hint,
    error,
    onDark = false,
    className,
    id: idProp,
    disabled,
    options,
    children,
    placeholder,
    "data-testid": testId,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? `select-${autoId.replace(/[^a-zA-Z0-9-]/g, "")}`;
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const describedBy = errorId || hintId;

  return (
    <div className={cn("grid gap-1.5", className)}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "text-[13px] font-semibold leading-tight",
            onDark ? "text-ink-primary" : "text-frac-purple",
          )}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={id}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          data-testid={testId}
          className={cn(
            "block h-10 w-full appearance-none rounded-[10px] border pr-10 pl-3 py-[9px] text-[15px] leading-tight transition-colors duration-fast ease-out",
            "focus:outline-none focus:ring-[3px]",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            onDark
              ? "bg-[rgba(237,241,245,0.04)] text-ink-primary border-hairline focus:border-frac-violet focus:ring-frac-violet/15"
              : "bg-white text-frac-black border-frac-gray-30 focus:border-frac-violet focus:ring-frac-violet/15",
            error &&
              "!border-[#A11C1C] focus:!border-[#A11C1C] focus:!ring-[#A11C1C]/15",
          )}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options
            ? options.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}
                </option>
              ))
            : children}
        </select>
        <ChevronDown
          className={cn(
            "pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2",
            onDark ? "text-ink-tertiary" : "text-frac-gray-50",
          )}
          strokeWidth={1.75}
          aria-hidden
        />
      </div>
      {(error || hint) && (
        <p
          id={errorId ?? hintId}
          data-testid={
            testId ? (error ? `${testId}-error` : `${testId}-hint`) : undefined
          }
          className={cn(
            "text-[12.5px] leading-snug",
            error
              ? "text-[#A11C1C]"
              : onDark
                ? "text-ink-tertiary"
                : "text-frac-gray-70",
          )}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});

export default Select;
